import type { FieldErrors } from 'react-hook-form'

// A flat, Mixpanel-safe view of react-hook-form's errors object (which holds
// refs and can be circular, so it can't be logged directly).
export interface SerializedFormError {
  field: string
  type: string
  message: string
}

export const serializeFormErrors = (
  errors: FieldErrors | undefined
): SerializedFormError[] => {
  return Object.entries(errors || {}).map(([field, error]: [string, any]) => ({
    field,
    type: error?.type ? String(error.type) : 'unknown',
    message: typeof error?.message === 'string' ? error.message : '',
  }))
}

export type DisabledReasonCode =
  | 'read-only'
  | 'loading'
  | 'invalid'
  | 'not-dirty'
  | null

export interface SubmitDisabledReason {
  // null means the button is enabled (nothing is holding it back).
  reason: DisabledReasonCode
  // Field names with errors, when the reason is 'invalid'.
  fields: string[]
}

interface DisabledReasonOptions {
  readOnly?: boolean
  loading?: boolean
  isNewEntity?: boolean
}

// Explains why a submit button is disabled, in the same order the button's
// own `disabled` expression evaluates it.
export const getSubmitDisabledReason = (
  formState: { isValid: boolean; isDirty: boolean; errors: FieldErrors },
  { readOnly = false, loading = false, isNewEntity = false }: DisabledReasonOptions
): SubmitDisabledReason => {
  const fields = Object.keys(formState.errors || {})

  if (readOnly) return { reason: 'read-only', fields }
  if (loading) return { reason: 'loading', fields }
  if (!formState.isValid) return { reason: 'invalid', fields }
  if (!isNewEntity && !formState.isDirty) return { reason: 'not-dirty', fields }

  return { reason: null, fields: [] }
}
